import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { EmrCheckboxComponent } from "../../../../shared/emr-checkbox/emr-checkbox.component";
import { EmrInputComponent } from "../../../../shared/emr-input/emr-input.component";
import { EmrCheckboxGroupComponent } from "../../../../shared/emr-checkbox-group/emr-checkbox-group.component";
import { AssessmentRowComponent } from "../assessment-row/assessment-row.component";
import { FormOption } from 'src/app/shared/models/form-option.model';
import { RespiratoryFormGroup } from 'src/app/calvin-emr-shared/models/assessments/respiratory.model';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { WnlGroupDirective } from '../wnl/wnl-group.directive';
import { WnlControlDirective } from '../wnl/wnl-control.directive';

@Component({
  selector: 'app-respiratory-form',
  templateUrl: './respiratory-form.component.html',
  styleUrls: ['./respiratory-form.component.scss', '../assessments-tab.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    EmrCheckboxComponent,
    EmrInputComponent,
    EmrCheckboxGroupComponent,
    AssessmentRowComponent,
    WnlGroupDirective,
    WnlControlDirective,
  ],
})
export class RespiratoryFormComponent implements OnInit {

  formGroup = input.required<RespiratoryFormGroup>();
  patient = input.required<Patient>();

  ngOnInit(): void {
    // Create listener on cough form control to reset cough Description form control value
    this.formGroup().valueChanges.subscribe(() => {
      // Only reset the other controls if the current value is false
      if (!this.isCoughPresent) {
        this.formGroup().controls.coughDescription.setValue('', { emitEvent: false });
        this.formGroup().controls.productiveDescription.setValue([], { emitEvent: false });
      }
    });

    // Clear the "Other Lung Sound" controls if the user switches from "Other" to a different option
    this.formGroup().controls.lungSoundsRul.valueChanges.subscribe((value: string | null) => {
      if (value !== 'Other') {
        this.formGroup().controls.lungSoundsRulOther.setValue('', { emitEvent: false });
      }
    });
    this.formGroup().controls.lungSoundsRml.valueChanges.subscribe((value: string | null) => {
      if (value !== 'Other') {
        this.formGroup().controls.lungSoundsRmlOther.setValue('', { emitEvent: false });
      }
    });
    this.formGroup().controls.lungSoundsRll.valueChanges.subscribe((value: string | null) => {
      if (value !== 'Other') {
        this.formGroup().controls.lungSoundsRllOther.setValue('', { emitEvent: false });
      }
    });
    this.formGroup().controls.lungSoundsLul.valueChanges.subscribe((value: string | null) => {
      if (value !== 'Other') {
        this.formGroup().controls.lungSoundsLulOther.setValue('', { emitEvent: false });
      }
    });
    this.formGroup().controls.lungSoundsLll.valueChanges.subscribe((value: string | null) => {
      if (value !== 'Other') {
        this.formGroup().controls.lungSoundsLllOther.setValue('', { emitEvent: false });
      }
    });
  }

  reset() {
    this.formGroup().enable({ emitEvent: false });
  }

  get isRetractionsPresent() {
    return !!this.formGroup().controls.retractionsPresent.value;
  }

  get isCoughPresent(): boolean {
    return !!this.formGroup().controls.cough.value;
  }

  get isOtherQuality() {
    return this.formGroup().controls.quality.value === 'Other';
  }

  get isOtherRUL() {
    return this.formGroup().controls.lungSoundsRul.value === 'Other';
  }

  get isOtherRLL() {
    return this.formGroup().controls.lungSoundsRll.value === 'Other';
  }

  get isOtherRML() {
    return this.formGroup().controls.lungSoundsRml.value === 'Other';
  }

  get isOtherLUL() {
    return this.formGroup().controls.lungSoundsLul.value === 'Other';
  }

  get isOtherLLL() {
    return this.formGroup().controls.lungSoundsLll.value === 'Other';
  }

  get isCoughProductive(): boolean {
    return this.formGroup().controls.coughDescription.value!.toLocaleLowerCase() === 'productive';
  }

  get isTrachPresent(): boolean {
    return !!this.formGroup().controls.trachPresent.value;
  }

  get isInnerCannulaChanged(): boolean {
    return !!this.formGroup().controls.innerCannulaChanged.value;
  }

  get isTrachTiesChanged(): boolean {
    return !!this.formGroup().controls.trachTiesChanged.value;
  }

  qualityOptions = [
    new FormOption('Dyspnea', 'dyspnea'),
    new FormOption('Tachypnea', 'tachypnea'),
    new FormOption('Bradypnea', 'bradypnea'),
    new FormOption('Apnea', 'apnea'),
    new FormOption('Stridor', 'stridor'),
    new FormOption('WNL'),
    new FormOption('Other')
  ];

  lungSoundOptions = [
    new FormOption('Coarse rhonchi'),
    new FormOption('Crackles/rales'),
    new FormOption('Wheezes expiratory'),
    new FormOption('Wheezes inspiratory'),
    new FormOption('Other'),
    new FormOption('WNL'),
  ];

  coughDescriptionOptions = [
    new FormOption('Productive'),
    new FormOption('Nonproductive'),
    new FormOption('Dry'),
  ];

  productiveDescriptionOptions = [
    new FormOption('Scant', 'scant'),
    new FormOption('Moderate', 'moderate'),
    new FormOption('Large', 'large'),
    new FormOption('Purulent', 'purulent'),
    new FormOption('Blood tinged', 'bloodTinged'),
    new FormOption('Black', 'black'),
    new FormOption('Green', 'green'),
    new FormOption('Yellow', 'yellow'),
    new FormOption('Thick', 'thick'),
    new FormOption('Thin', 'thin'),
    new FormOption('Tenacious', 'tenacious'),
  ];
}
